// Largest Triangle Three Buckets — algoritmo classico de downsample
// pra series temporais. Implementacao do paper de Sveinn Steinarsson
// (2013) — preserva visualmente picos/vales/transicoes da curva
// original.
//
// Por que LTTB: Recharts/SVG performam mal alem de ~5k pontos — render
// inicial trava, hover laggy, brush gruda. Com 24h de sampling 1s sao
// 86k pontos por variavel. LTTB reduz pra ~500-1000 pontos com a
// curva quase indistinguivel da raw (preserva extremos locais).
//
// Importante: usar so na RENDERIZACAO. Stats (mean, stddev, outliers,
// percentis) continuam vindo dos dados raw — downsampling muda a
// distribuicao estatistica.

// Calcula a area do triangulo formado por 3 pontos. Usada como
// criterio LTTB: maior triangulo = ponto mais "informativo" do bucket.
const triangleArea = (p1, p2, p3) =>
  Math.abs(
    (p1.x - p3.x) * (p2.y - p1.y) - (p1.x - p2.x) * (p3.y - p1.y)
  ) * 0.5;

// data: array com [{ timestamp, value, ...rest }] (timestamp number,
// value number, demais campos preservados pro Recharts/tooltip).
// targetPoints: alvo de output. LTTB nao garante exato mas chega
// MUITO proximo (target ± 2).
//
// Retorna: array com mesma shape, length ~ targetPoints. Primeiro e
// ultimo ponto sempre preservados.
export const downsampleLTTB = (data, targetPoints) => {
  if (!Array.isArray(data) || data.length <= targetPoints || targetPoints < 3) {
    return data;
  }

  // Helper: extrai (x, y) numericos do ponto mantendo referencia ao
  // objeto original (pra preservar todos os campos via index lookup).
  const toXY = (point, idx) => ({
    x: Number(point.timestamp) || idx,
    y: Number(point.value),
    idx,
  });

  // Bucket size (excluindo primeiro/ultimo pontos que sao fixos).
  const bucketSize = (data.length - 2) / (targetPoints - 2);

  const sampled = [data[0]]; // primeiro ponto sempre

  let aIdx = 0; // indice do ponto "anchor" (escolhido no bucket anterior)

  for (let i = 0; i < targetPoints - 2; i += 1) {
    // Range do bucket atual
    const bucketStart = Math.floor((i + 0) * bucketSize) + 1;
    const bucketEnd = Math.floor((i + 1) * bucketSize) + 1;

    // Range do PROXIMO bucket — usado pra calcular media (ponto
    // virtual do bucket seguinte).
    const nextBucketStart = bucketEnd;
    const nextBucketEnd = Math.min(
      Math.floor((i + 2) * bucketSize) + 1,
      data.length
    );

    // Media (x, y) do proximo bucket — vertice 3 do triangulo
    let avgX = 0;
    let avgY = 0;
    let avgCount = 0;
    for (let j = nextBucketStart; j < nextBucketEnd; j += 1) {
      const p = toXY(data[j], j);
      if (Number.isFinite(p.y)) {
        avgX += p.x;
        avgY += p.y;
        avgCount += 1;
      }
    }
    if (avgCount === 0) {
      // Fallback: bucket inteiro com NaN — pega o primeiro do bucket
      // pra nao quebrar o algoritmo
      sampled.push(data[bucketStart]);
      aIdx = bucketStart;
      continue; // eslint-disable-line no-continue
    }
    avgX /= avgCount;
    avgY /= avgCount;

    // Anchor do bucket anterior — vertice 1 do triangulo
    const anchor = toXY(data[aIdx], aIdx);

    // Acha o ponto do bucket atual que forma o maior triangulo
    // (anchor, candidato, media_proximo_bucket).
    let maxArea = -1;
    let maxIdx = bucketStart;
    for (let j = bucketStart; j < bucketEnd && j < data.length; j += 1) {
      const cand = toXY(data[j], j);
      if (!Number.isFinite(cand.y)) continue; // eslint-disable-line no-continue
      const area = triangleArea(anchor, cand, { x: avgX, y: avgY });
      if (area > maxArea) {
        maxArea = area;
        maxIdx = j;
      }
    }

    sampled.push(data[maxIdx]);
    aIdx = maxIdx;
  }

  sampled.push(data[data.length - 1]); // ultimo ponto sempre
  return sampled;
};
